import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  SecurityContext,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { displayedSenderName } from 'src/app/utils/deleted-user';
import { Subscription } from 'rxjs';
import { CrwdAvatar, CrwdIcon, CrwdModal, CrwdSpinner, CrwdTooltip } from '@crewdle/ui-kit';

import { ChatSidebarService, SidebarChatItem } from 'src/app/services/chat-sidebar/chat-sidebar.service';
import { GlobalSearchService } from 'src/app/services/global-search/global-search.service';
import { SearchResult, SearchService, SearchSource } from 'src/app/services/search/search.service';
import { SidebarListsService } from 'src/app/services/sidebar-lists/sidebar-lists.service';
import { SidebarNavService } from 'src/app/services/sidebar-nav/sidebar-nav.service';
import { TeamsService } from 'src/app/services/teams/teams.service';
import { agentColorFor } from 'src/app/utils/agent-color';
import { formatHitTime, rankByName, resolveMessageRoute } from './global-search.utils';

type SearchRow =
  | { kind: 'chat'; id: string; index: number; title: string; chatId: string }
  | { kind: 'agent'; id: string; index: number; title: string; conversationId: string; badgeKey: string | null; color: string }
  | { kind: 'team'; id: string; index: number; title: string; key: string; managerName: string; memberCount: number; color: string }
  | { kind: 'message'; id: string; index: number; result: SearchResult };

interface SearchGroup {
  kind: SearchRow['kind'];
  labelKey: string;
  rows: SearchRow[];
}

const ENTITY_LIMIT = 6;

/**
 * The one search surface of the app: a command palette over everything the
 * user can reach — personal chats (when the Chat product is on), hired agents,
 * teams, and message contents from both products (via `searchMessages`).
 *
 * Hosted once in the shell (`app.component.html`); opened by the rail's Search
 * row or Cmd/Ctrl+K on any route (`GlobalSearchService`). Names filter locally
 * from the rosters the rail already holds, so they appear as you type;
 * messages come back from the index a debounce later, below them.
 *
 * When opened from inside a conversation, an "Only in <name>" chip narrows the
 * message results to it — off by default, so the palette always starts global.
 */
@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [TranslateModule, CrwdAvatar, CrwdIcon, CrwdModal, CrwdSpinner, CrwdTooltip],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <crwd-modal
      [open]="search.open()"
      size="xl"
      [mobileFullscreen]="true"
      bodyScroll="content"
      [showCloseButton]="false"
      (closed)="search.close()"
    >
      <div
        data-testid="global-search"
        class="global-search-root flex h-full flex-col overflow-hidden py-5 text-text md:-m-6 md:h-auto md:max-h-[calc(90vh-2px)]"
      >
        <div class="px-4 pb-2.5">
          <div class="flex items-center gap-2 px-2">
            <crwd-icon name="search" size="18px" class="shrink-0 text-text-secondary" />
            <input
              #searchInput
              type="text"
              autocomplete="off"
              spellcheck="false"
              class="w-full bg-transparent py-1 text-base text-text outline-none placeholder:text-text-secondary md:text-sm"
              [placeholder]="'search.placeholder' | translate"
              [value]="query()"
              (input)="onQueryChange($any($event.target).value)"
            />
            @if (query()) {
              <button
                type="button"
                class="shrink-0 cursor-pointer text-text-secondary transition-colors hover:text-text"
                [attr.aria-label]="'search.clear-query' | translate"
                [crwdTooltip]="'search.clear-query' | translate"
                (click)="clearQuery()"
              >
                <crwd-icon name="x-circle" size="16px" />
              </button>
            }
            <span class="mx-1 h-5 w-px shrink-0 bg-border" aria-hidden="true"></span>
            <button
              type="button"
              class="inline-flex shrink-0 cursor-pointer items-center rounded px-1.5 py-0.5 text-xs leading-4
                text-text-secondary transition-colors hover:bg-surface hover:text-text"
              [attr.aria-label]="'search.close-search' | translate"
              [crwdTooltip]="'search.close-search' | translate"
              (click)="search.close()"
            >
              <span class="hidden md:inline">Esc</span>
              <!-- The wrapper owns the breakpoint: crwd-icon's own :host display wins over a class on the icon. -->
              <span class="inline-flex md:hidden" aria-hidden="true"><crwd-icon name="x" size="16px" /></span>
            </button>
          </div>
          @if (currentConversation(); as scope) {
            <div class="mt-2 px-2">
              <button
                type="button"
                [class]="chipClass(scopeOn())"
                [attr.aria-pressed]="scopeOn()"
                (click)="toggleScope()"
              >
                <crwd-icon name="message-circle" size="14px" />
                {{ 'search.only-in' | translate: { name: scope.name } }}
              </button>
            </div>
          }
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto px-4 pb-3 md:h-[34rem] md:flex-none">
          @if (!query().trim()) {
            <div class="flex flex-col items-center gap-2 px-3 py-12 text-center text-sm text-text-secondary">
              <span>{{ 'search.hint' | translate }}</span>
              <kbd class="hidden rounded border border-border px-1.5 py-0.5 text-[11px] md:inline-block">{{ shortcutLabel }}</kbd>
            </div>
          } @else {
            @for (group of groups(); track group.kind) {
              <div class="px-3 pb-1 pt-3 text-xs font-medium uppercase tracking-wide text-text-secondary">
                {{ group.labelKey | translate }}
                @if (group.kind === 'message' && totalFound() > group.rows.length) {
                  <span class="normal-case tracking-normal">· {{ totalFound() }}</span>
                }
              </div>
              @for (row of group.rows; track row.id) {
                <button
                  type="button"
                  [attr.data-search-selected]="selectedIdx() === row.index ? 'true' : null"
                  [class]="rowClass(selectedIdx() === row.index)"
                  (mouseenter)="setSelected(row.index)"
                  (click)="activate(row)"
                >
                  @switch (row.kind) {
                    @case ('chat') {
                      <crwd-icon name="message-circle" size="18px" class="shrink-0 text-text-secondary" />
                      <span class="flex-1 truncate text-left">{{ row.title || ('sidebar.new-chat' | translate) }}</span>
                    }
                    @case ('agent') {
                      <crwd-avatar [name]="row.title || '?'" [color]="row.color" [size]="22" shape="circle" class="shrink-0" />
                      <span class="flex-1 truncate text-left">{{ row.title }}</span>
                      @if (row.badgeKey) {
                        <span class="shrink-0 text-xs text-text-secondary">{{ row.badgeKey | translate }}</span>
                      }
                    }
                    @case ('team') {
                      <crwd-avatar [name]="row.title || '?'" [color]="row.color" [size]="22" shape="square" class="shrink-0" />
                      <span class="flex-1 truncate text-left">{{ row.title }}</span>
                      <span class="shrink-0 truncate text-xs text-text-secondary">{{ row.managerName }}</span>
                    }
                    @case ('message') {
                      <crwd-icon name="messages-square" size="18px" class="mt-0.5 shrink-0 self-start text-text-secondary" />
                      <span class="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left">
                        <span class="flex w-full items-baseline gap-2 text-xs text-text-secondary">
                          <span class="truncate font-medium text-text">{{ senderLabel(row.result) }}</span>
                          <span class="truncate">{{ locationLabel(row.result) }}</span>
                          <span class="ml-auto shrink-0">{{ timeLabel(row.result) }}</span>
                        </span>
                        <span
                          class="line-clamp-2 w-full text-sm [&_mark]:rounded-sm [&_mark]:bg-[var(--accent-color)]/20 [&_mark]:text-text"
                          [innerHTML]="preview(row.result)"
                        ></span>
                      </span>
                    }
                  }
                </button>
              }
            }

            @if (messagesLoading()) {
              <div class="flex justify-center px-3 py-4">
                <crwd-spinner size="sm" [ariaLabel]="'search.searching' | translate" />
              </div>
            } @else if (hasMoreMessages()) {
              <button
                type="button"
                class="mx-3 my-2 w-[calc(100%-1.5rem)] cursor-pointer rounded-lg border border-border px-3 py-2 text-sm
                       text-text-secondary transition-colors hover:bg-surface hover:text-text"
                (click)="loadMore()"
              >
                {{ 'search.load-more' | translate }}
              </button>
            }

            @if (!messagesLoading() && groups().length === 0) {
              <div class="px-3 py-12 text-center text-sm text-text-secondary">
                {{ 'search.no-results' | translate: { query: query().trim() } }}
              </div>
            }
          }
        </div>
      </div>
    </crwd-modal>
  `,
})
export class GlobalSearchComponent {
  readonly search = inject(GlobalSearchService);
  private readonly nav = inject(SidebarNavService);
  private readonly lists = inject(SidebarListsService);
  private readonly teams = inject(TeamsService);
  private readonly chatSidebar = inject(ChatSidebarService);
  private readonly messages = inject(SearchService);
  private readonly router = inject(Router);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly translate = inject(TranslateService);

  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  readonly query = signal('');
  /** Index across the flattened groups; null = nothing selected (input focused). */
  readonly selectedIdx = signal<number | null>(null);
  readonly scopeOn = signal(false);
  readonly currentConversation = signal<{ id: string; name: string } | null>(null);
  readonly messageResults = signal<SearchResult[]>([]);
  readonly totalFound = signal(0);
  readonly messagesLoading = signal(false);
  readonly shortcutLabel = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘ K' : 'Ctrl K';

  private readonly chatAllowed = toSignal(this.chatSidebar.allowed$, { initialValue: null });
  private readonly roster = toSignal(this.chatSidebar.roster$, { initialValue: null });
  private readonly folderChats = toSignal(this.chatSidebar.folderChats$, { initialValue: {} as Record<string, SidebarChatItem[]> });
  private readonly agents = toSignal(this.lists.agents$, { initialValue: null });
  private readonly sharedAgents = toSignal(this.lists.sharedAgents$, { initialValue: null });
  private readonly spaces = toSignal(this.lists.spaces$, { initialValue: null });
  private readonly teamRows = toSignal(this.teams.sidebarTeams$, { initialValue: null });
  /** Every visible chat, fetched once per open — the rail roster only windows the recent ones. */
  private readonly allChats = signal<SidebarChatItem[] | null>(null);

  private page = 1;
  private readonly subscriptions: Subscription[] = [];

  /** Chats to match names against: the full list once loaded, the rail's window meanwhile. */
  private readonly chatCandidates = computed<SidebarChatItem[]>(() => {
    const all = this.allChats();
    if (all) return all;
    const fromRoster = (this.roster() ?? []).filter((row) => row.kind === 'chat').map((row) => row.chat!);
    const fromFolders = Object.values(this.folderChats()).flat();
    return [...fromRoster, ...fromFolders];
  });

  readonly groups = computed<SearchGroup[]>(() => {
    const q = this.query().trim();
    if (!q) return [];
    const groups: SearchGroup[] = [];
    let index = 0;
    const push = (kind: SearchRow['kind'], labelKey: string, rows: Omit<SearchRow, 'index'>[]) => {
      if (!rows.length) return;
      groups.push({ kind, labelKey, rows: rows.map((row) => ({ ...row, index: index++ }) as SearchRow) });
    };

    if (!this.scopeOn()) {
      if (this.chatAllowed()) {
        push('chat', 'search.chats', rankByName(this.chatCandidates(), q, (c) => c.title, ENTITY_LIMIT)
          .map((c) => ({ kind: 'chat' as const, id: `chat:${c.chatId}`, title: c.title, chatId: c.chatId })));
      }
      const owned = (this.agents() ?? []).map((a) => ({
        kind: 'agent' as const,
        id: `agent:${a.conversationId}`,
        title: a.name,
        conversationId: a.conversationId,
        badgeKey: a.rented ? 'messaging.agents.rented-badge' : null,
        color: agentColorFor(a.name),
      }));
      const shared = (this.sharedAgents() ?? []).map((a) => ({
        kind: 'agent' as const,
        id: `agent:${a.conversationId}`,
        title: a.name,
        conversationId: a.conversationId,
        badgeKey: 'messaging.agents.shared-badge',
        color: agentColorFor(a.name),
      }));
      push('agent', 'search.agents', rankByName([...owned, ...shared], q, (a) => a.title, ENTITY_LIMIT));
      push('team', 'search.teams', rankByName(this.teamRows() ?? [], q, (t) => t.name, ENTITY_LIMIT).map((t) => ({
        kind: 'team' as const,
        id: `team:${t.key}`,
        title: t.name,
        key: t.key,
        managerName: t.managerName,
        memberCount: t.memberCount,
        color: agentColorFor(t.managerName || t.name),
      })));
    }

    push('message', 'search.messages', this.messageResults().map((result) => ({
      kind: 'message' as const,
      id: `message:${result.conversationId}:${result.messageId}`,
      result,
    })));
    return groups;
  });

  readonly flatRows = computed<SearchRow[]>(() => this.groups().flatMap((group) => group.rows));
  readonly hasMoreMessages = computed(() => this.messageResults().length < this.totalFound());

  constructor() {
    this.subscriptions.push(
      this.messages.results$.subscribe((response) => {
        if (!this.search.open()) return;
        this.messageResults.set(response.page > 1 ? [...this.messageResults(), ...response.results] : response.results);
        this.totalFound.set(response.totalFound);
        this.page = response.page;
      }),
      this.messages.loading$.subscribe((loading) => this.messagesLoading.set(loading)),
    );
    inject(DestroyRef).onDestroy(() => this.subscriptions.forEach((s) => s.unsubscribe()));

    effect(() => {
      const open = this.search.open();
      untracked(() => (open ? this.onOpen() : this.reset()));
    });
  }

  // ── Input ──

  onQueryChange(value: string): void {
    this.query.set(value);
    this.selectedIdx.set(null);
    this.runMessageSearch(1);
  }

  clearQuery(): void {
    this.onQueryChange('');
    this.searchInput?.nativeElement.focus();
  }

  toggleScope(): void {
    this.scopeOn.update((on) => !on);
    this.selectedIdx.set(null);
    this.runMessageSearch(1);
  }

  loadMore(): void {
    if (this.hasMoreMessages() && !this.messagesLoading()) this.runMessageSearch(this.page + 1);
  }

  // ── Rows ──

  rowClass(selected: boolean): string {
    return `flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-text transition-colors cursor-pointer ${
      selected ? 'bg-surface' : 'hover:bg-surface'
    }`;
  }

  chipClass(on: boolean): string {
    const base = 'inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors';
    return on
      ? `${base} border-transparent bg-[var(--accent-color)] text-white`
      : `${base} border-border text-text-secondary hover:text-text`;
  }

  setSelected(index: number): void {
    if (this.selectedIdx() !== index) this.selectedIdx.set(index);
  }

  senderLabel(result: SearchResult): string {
    if (result.source === 'chat' && result.sender === 'assistant') return this.translate.instant('search.assistant');
    if (result.senderName) return displayedSenderName(result.senderName, this.translate.instant('common.deleted-user'));
    const sender = result.sender || '';
    return sender.startsWith('agent:') ? result.conversationName || '' : sender;
  }

  locationLabel(result: SearchResult): string {
    if (result.channelName) return `#${result.channelName}`;
    if (result.conversationName) return result.conversationName;
    return this.translate.instant(result.source === 'chat' ? 'sidebar.new-chat' : 'search.direct-message');
  }

  timeLabel(result: SearchResult): string {
    return formatHitTime(result.createdOn);
  }

  /** The index has no highlights of its own beyond the server-built snippet; keep only its <mark>s. */
  preview(result: SearchResult): SafeHtml {
    const highlight = result.highlights?.find((h) => h.field === 'content' || h.field === 'contentPlain');
    let raw = highlight?.snippet || '';
    if (!raw) {
      const text = result.contentPlain || result.content || '';
      raw = text.length > 150 ? `${text.slice(0, 150)}…` : text;
    }
    return this.sanitizer.bypassSecurityTrustHtml(this.sanitizer.sanitize(SecurityContext.HTML, raw) || '');
  }

  activate(row: SearchRow): void {
    switch (row.kind) {
      case 'chat':
        this.nav.openChat(row.chatId);
        break;
      case 'agent':
        this.nav.openAgentConversation(row.conversationId);
        break;
      case 'team':
        this.nav.openTeamByKey(row.key);
        break;
      case 'message': {
        const target = resolveMessageRoute(row.result, {
          teamConversationIds: new Set((this.teamRows() ?? []).map((t) => t.conversationId)),
          spaceConversationIds: new Set((this.spaces() ?? []).map((s) => s.conversationId)),
        });
        void this.router.navigate(target.commands, { queryParams: target.queryParams });
        this.nav.closeMobile();
        break;
      }
    }
    this.search.close();
  }

  // ── Keyboard ──

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.search.toggle();
      return;
    }
    if (!this.search.open()) return;
    // Esc is the modal's own.
    const total = this.flatRows().length;
    if (total === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const current = this.selectedIdx();
      this.setSelected(Math.min((current === null ? -1 : current) + 1, total - 1));
      this.scrollSelectedIntoView();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const current = this.selectedIdx();
      if (current === null || current === 0) {
        this.selectedIdx.set(null);
        this.searchInput?.nativeElement.focus();
        return;
      }
      this.setSelected(current - 1);
      this.scrollSelectedIntoView();
    } else if (event.key === 'Enter') {
      const current = this.selectedIdx();
      const row = current === null ? this.flatRows()[0] : this.flatRows()[current];
      if (row) {
        event.preventDefault();
        this.activate(row);
      }
    }
  }

  private scrollSelectedIntoView(): void {
    queueMicrotask(() =>
      document.querySelector('[data-search-selected="true"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' }),
    );
  }

  // ── Lifecycle ──

  private onOpen(): void {
    this.currentConversation.set(this.detectCurrentConversation());
    if (this.chatAllowed()) {
      void this.chatSidebar.listAllChats().then((chats) => {
        if (this.search.open()) this.allChats.set(chats);
      });
    }
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  private reset(): void {
    this.query.set('');
    this.selectedIdx.set(null);
    this.scopeOn.set(false);
    this.allChats.set(null);
    this.messageResults.set([]);
    this.totalFound.set(0);
    this.page = 1;
    this.messages.clearResults();
  }

  private runMessageSearch(page: number): void {
    const q = this.query().trim();
    if (page === 1) {
      this.messageResults.set([]);
      this.totalFound.set(0);
      this.page = 1;
    }
    const sources: SearchSource[] = this.chatAllowed() ? ['connect', 'chat'] : ['connect'];
    const conversationId = this.scopeOn() ? this.currentConversation()?.id : undefined;
    this.messages.search(q, { conversationId, page, sources });
  }

  /** The conversation on screen when the palette opened, if any — the "Only in" chip's target. */
  private detectCurrentConversation(): { id: string; name: string } | null {
    const tree = this.router.parseUrl(this.router.url);
    const segments = tree.root.children.primary?.segments.map((s) => s.path) ?? [];
    const params: Record<string, string | undefined> = tree.queryParams;
    let id: string | null = null;
    if (segments[0] === 'agents' && params.conversationId) id = params.conversationId;
    else if (segments[0] === 'messaging') id = segments[1] === 'conversation' ? segments[2] ?? null : params.conversationId ?? null;
    if (!id) return null;

    const name = (this.agents() ?? []).find((a) => a.conversationId === id)?.name
      || (this.sharedAgents() ?? []).find((a) => a.conversationId === id)?.name
      || (this.teamRows() ?? []).find((t) => t.conversationId === id)?.name
      || (this.spaces() ?? []).find((s) => s.conversationId === id)?.name
      || this.translate.instant('search.this-conversation');
    return { id, name };
  }
}
